/**
 * Compress and optimize an image file before upload
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxSizeMB - Maximum file size in MB (default: 3)
 * @param {number} options.maxWidthOrHeight - Maximum width or height in pixels (default: 1920)
 * @param {number} options.quality - Image quality 0-1 (default: 0.8)
 * @returns {Promise<File>} - Compressed image file
 */
export const compressImage = async (file, options = {}) => {
  const {
    maxSizeMB = 3,
    maxWidthOrHeight = 1920,
    quality = 0.8
  } = options;

  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      reject(new Error('File must be an image'));
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;

      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxWidthOrHeight) {
            height = Math.round((height * maxWidthOrHeight) / width);
            width = maxWidthOrHeight;
          }
        } else {
          if (height > maxWidthOrHeight) {
            width = Math.round((width * maxWidthOrHeight) / height);
            height = maxWidthOrHeight;
          }
        }

        // Create canvas and compress
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Try different quality levels to meet size requirement
        const tryCompress = (currentQuality) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              const sizeInMB = blob.size / (1024 * 1024);

              // If size is acceptable or quality is already very low, return result
              if (sizeInMB <= maxSizeMB || currentQuality <= 0.1) {
                const compressedFile = new File(
                  [blob],
                  file.name,
                  {
                    type: file.type,
                    lastModified: Date.now(),
                  }
                );
                resolve(compressedFile);
              } else {
                // Try with lower quality
                tryCompress(currentQuality - 0.1);
              }
            },
            file.type,
            currentQuality
          );
        };

        tryCompress(quality);
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
  });
};

/**
 * Validate image file before compression
 * @param {File} file - The image file to validate
 * @param {number} maxSizeMB - Maximum file size in MB
 * @returns {Object} - Validation result with isValid and error properties
 */
export const validateImageFile = (file, maxSizeMB = 3) => {
  if (!file) {
    return { isValid: false, error: 'No file selected' };
  }

  if (!file.type.startsWith('image/')) {
    return { isValid: false, error: 'File must be an image' };
  }

  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: 'Only JPG, PNG, and WebP images are allowed' };
  }

  const sizeInMB = file.size / (1024 * 1024);
  if (sizeInMB > 10) { // Allow up to 10MB before compression
    return { isValid: false, error: `File size must be less than 10MB (current: ${sizeInMB.toFixed(2)}MB)` };
  }

  return { isValid: true, error: null };
};
